import ProspectStageType from '@/Mapper/ProspectStageType';
import ProspectStatusType from '@/Mapper/ProspectStatusType';
import { calcDaysAgo } from '@/filters/date-filters';
import {
  ProspectActivityActionTypeId,
  ProspectPropertyRelationshipId,
  ProspectReminderTypeId,
  OaLeadSourceTypeId,
  ProspectActivityActorTypeId,
} from '@/Mapper/ProspectActivityActionType';

const sortProspectData = prospect => prospect.data.sort(
  (a, b) => {
    if (a.contactBadge.orderId > b.contactBadge.orderId) {
      return 1;
    }

    if (a.contactBadge.orderId === b.contactBadge.orderId
      && new Date(a.createdDate) > new Date(b.createdDate)) {
      return -1;
    }

    return -1;
  },
);

const hasNotAttempted = (stage) => {
  if (stage.stageType.stageTypeId === ProspectStageType.new) {
    const daysAgo = calcDaysAgo(
      stage.createdDate,
      true,
    );

    if (daysAgo > 1) {
      return true;
    }
  }

  return false;
};

const prospectHasStage = (prospectStages, stageTypeId) => prospectStages != null
  && prospectStages.filter(stage => stage.stageType.stageTypeId === stageTypeId).length > 0;

const orderProspectsbyCreatedDate = prospects => prospects.sort(
  (a, b) => new Date(b.createdDate) - new Date(a.createdDate),
);

const isOaAssistantActivity = (activity) => {
  if(!activity) return false;
  const mediumType = activity.mediumType ? activity.mediumType.activityMediumTypeId : null;
  const creatorActorTypeId = activity.creatorActorType
    ? activity.creatorActorType.activityActorTypeId : null;

  if (activity && mediumType === 8 && activity.name === 'OA Assistant'
    && creatorActorTypeId === ProspectActivityActorTypeId.oaConsultant) {
    return true;
  }

  return false;
};

const getProspectsTopActivity = (activities) => {
  let topActivity = null;

  if (activities.length > 0) {
    // Find most recent oaAssistant
    topActivity = activities.find(isOaAssistantActivity);

    if (!topActivity) {
    // Find most recent requested Appraisal
      topActivity = activities.find(activity => activity.actionType.activityActionTypeId
  === ProspectActivityActionTypeId.requestedAppraisal);
    }

    // If no requested Appraisal was found
    if (!topActivity) {
      // Find an OAMatch
      topActivity = activities.find(activity => activity.actionType.activityActionTypeId
      === ProspectActivityActionTypeId.oaMatch);
    }

    // If no requested Appraisal was found
    if (!topActivity) {
      // Find most recent property estimate where they are the owner
      topActivity = activities.find(activity => activity.actionType.activityActionTypeId
    === ProspectActivityActionTypeId.propertyEstimate
    && (activity.metaData.propertyRelationship.propertyRelationshipId
            === ProspectPropertyRelationshipId.ownerOccupier
          || activity.metaData.propertyRelationship.propertyRelationshipId
             === ProspectPropertyRelationshipId.ownerInvestor));
    }
  }
  return topActivity;
};

const getProspectsActivteReminder = (reminders) => {
  if (reminders.length > 0) {
    const filteredReminders = reminders
      .filter(r => r.completedDate === null)
      .sort((a, b) => new Date(b.reminderDate) - new Date(a.reminderDate));

    if (filteredReminders.length > 0) {
      return filteredReminders[0];
    }
  }
  return null;
};

const getProspectBadgeText = (prospect, activeReminder) => {
  // If: Prospect has been removed - over rides any stage changes or nextView
  if (prospect.status.statusType.statusTypeId === ProspectStatusType.removed) {
    return prospect.status.statusType.type;
  }

  if (activeReminder) {
    switch (activeReminder.reminderType.reminderTypeId) {
      case ProspectReminderTypeId.bookedAppraisal:
        return 'Booked appraisal';
      case ProspectReminderTypeId.followUp:
        return 'Follow-up booked';
      default:
        return null;
    }
  }

  if (hasNotAttempted(prospect.stage)) {
    return 'Not attempted';
  }

  return prospect.stage.stageType ? prospect.stage.stageType.type : null;
};

const getProspectStageBadge = (prospect) => {
  if (prospect.status.statusType.statusTypeId === ProspectStatusType.removed) {
    return prospect.status.statusType.type;
  }

  if (hasNotAttempted(prospect.stage)) {
    return 'Not attempted';
  }

  return prospect.stage.stageType ? prospect.stage.stageType.type : null;
};

const getProspectActionBadge = (prospect, activeReminder) => {
  if (activeReminder) {
    switch (activeReminder.reminderType.reminderTypeId) {
      case ProspectReminderTypeId.bookedAppraisal:
        return 'Appraise';
      case ProspectReminderTypeId.followUp:
        return 'Follow-up';
      default:
        return null;
    }
  }
  return null;
};

const getPropsectActivitySummary = (activity) => {
  if (activity) {
    switch (activity.actionType.activityActionTypeId) {
      case ProspectActivityActionTypeId.requestedAppraisal:
        return 'Requested appraisal';
      case ProspectActivityActionTypeId.oaMatch:

        if (activity.rawMetaData.lead.leadSourceTypeId
          === OaLeadSourceTypeId.propertyReports) {
          return 'Received property report';
        }

        if (activity.rawMetaData.lead.leadSourceTypeId
          === OaLeadSourceTypeId.openEstimates) {
          return 'Received property estimate';
        }

        // SmartSearch or PowerBy
        return 'Researching agents';
      case ProspectActivityActionTypeId.propertyEstimate:
        return 'Received property estimate';
      default:
        break;
    }

    if (isOaAssistantActivity(activity)) {
      return 'OpenAgent verified this seller';
    }
  }

  return null;
};

export default {
  sortProspectData,
  prospectHasStage,
  orderProspectsbyCreatedDate,
  getProspectsTopActivity,
  getProspectsActivteReminder,
  hasNotAttempted,
  getProspectBadgeText,
  getProspectStageBadge,
  getProspectActionBadge,
  getPropsectActivitySummary,
  isOaAssistantActivity,
};
